import type { Node } from "@xyflow/react";
import { useCanvasStore } from "@/stores/canvas-store";
import type { PresentationDimsSnapshot } from "@/lib/focus/focus-types";
import type { FrameData } from "@/lib/frame-data";
import { isTransientConversationFrameNode } from "@/lib/transient-conversation-frame";

/**
 * React Flow can emit delayed ResizeObserver dimension updates after an
 * ephemeral presentation resize. Keep persistence suppressed long enough for
 * those echoes to settle without blocking a later user resize indefinitely.
 */
const SUPPRESSION_FALLBACK_MS = 2000;
const suppressionTimers = new Map<string, ReturnType<typeof setTimeout>>();

export function suppressPresentationDimensionPersistence(frameId: string) {
  const previousTimer = suppressionTimers.get(frameId);
  if (previousTimer !== undefined) clearTimeout(previousTimer);
  useCanvasStore.getState().addPresentationDimsSuppressed(frameId);
  const timer = setTimeout(() => {
    if (suppressionTimers.get(frameId) !== timer) return;
    suppressionTimers.delete(frameId);
    useCanvasStore.getState().clearPresentationDimsSuppressed(frameId);
  }, SUPPRESSION_FALLBACK_MS);
  suppressionTimers.set(frameId, timer);
}

/** A node back at the workspace geometry a presentation snapshotted. */
export function withPresentationDims<T extends Node<FrameData>>(
  node: T,
  snapshot: PresentationDimsSnapshot,
): T {
  return {
    ...node,
    position: snapshot.position ?? node.position,
    width: snapshot.width,
    height: snapshot.height,
    style: snapshot.style,
    measured: snapshot.measured,
    className: snapshot.className,
  };
}

/**
 * The snapshots a restore may write: a temporary conversation keeps its
 * focus geometry, and the echo of every restored node is not persisted.
 */
export function restorablePresentationDims(
  snapshots: PresentationDimsSnapshot[],
): Map<string, PresentationDimsSnapshot> {
  const nodes = new Map(
    useCanvasStore.getState().nodes.map((node) => [node.id, node]),
  );
  const restorable = snapshots.filter((snapshot) => {
    const node = nodes.get(snapshot.frameId);
    return !node || !isTransientConversationFrameNode(node);
  });
  const byFrameId = new Map<string, PresentationDimsSnapshot>();
  for (const snapshot of restorable) {
    suppressPresentationDimensionPersistence(snapshot.frameId);
    if (!byFrameId.has(snapshot.frameId)) {
      byFrameId.set(snapshot.frameId, snapshot);
    }
  }
  return byFrameId;
}

/** Restore every ephemeral presentation resize from the original snapshot. */
export function restorePresentationDimensions() {
  const restore = useCanvasStore.getState().presentationRestore;
  if (!restore || restore.dims.length === 0) return;
  const snapshots = restorablePresentationDims(restore.dims);
  useCanvasStore.setState((current) => ({
    nodes: current.nodes.map((node) => {
      const snapshot = snapshots.get(node.id);
      return snapshot ? withPresentationDims(node, snapshot) : node;
    }),
    presentationRestore: current.presentationRestore && {
      ...current.presentationRestore,
      dims: [],
    },
  }));
}
